/**
 * どの機能にも属さないルートの定義（ホーム・廃止した横断検索）。どちらも PJ 一覧へ倒す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ホームと廃止した横断検索のルートを定義し、どちらも PJ 一覧へ倒す。
 * @graph-connects none
 */
import { Navigate, createRoute } from '@tanstack/react-router';
import { HOME_ROUTE, PJS_ROUTE, SEARCH_ROUTE } from '../route-paths.js';
import { rootRoute } from './root.js';

/**
 * ホームは PJ 一覧へ倒す。
 *
 * ルートを消さずに倒すのは、`/` を開いた既存のブックマークを 404 にしないためである
 * （`frontend.md` の「深いリンクから到達した URL は 404 を出さない」）。
 *
 * `beforeLoad` で `redirect` を投げる形は採らない。あれは Error ではない値を throw する
 * 書き方で、`only-throw-error` に当たる。ルールを黙らせるより、描画側で行き先を宣言する
 * ほうが「この URL には中身が無い」ことがコードの形として残る。
 * @graph-connects none
 */
export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: HOME_ROUTE,
  component: () => <Navigate to={PJS_ROUTE} replace />,
});
/**
 * 廃止した横断検索も PJ 一覧へ倒す。
 *
 * 廃止前の横断検索は結果 URL を共有できるよう検索語を検索パラメータに置いていたので、
 * `/search?q=...` は共有・ブックマークされている前提で扱う。ホームと同じくルートを残す。
 * 検索パラメータは検証せず捨てる。行き先の一覧が型ごとに分かれており、検索語の引き継ぎ先が
 * 一意に決まらないためである。
 * @graph-connects none
 */
export const searchRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SEARCH_ROUTE,
  component: () => <Navigate to={PJS_ROUTE} replace />,
});

/** どの機能にも属さないルート。ルート木（`app.tsx`）はこれを並べる。 @graph-connects none */
export const commonRoutes = [indexRoute, searchRedirectRoute] as const;
